/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/member-ordering */
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { DropDownOption, DropDownType, GroupedDropDownOption, ValueChangeEvent } from '@interfaces/common/form.interface';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged } from 'rxjs/operators';
import { InputComponent } from '../input/input.component';

@Component({
  selector: 'app-single-selection',
  templateUrl: './single-selection.component.html',
  styleUrls: ['./single-selection.component.scss'],
})
export class SingleSelectionComponent extends InputComponent implements OnInit {

  @ViewChild('input', {static: true})
  inputElRef!: ElementRef;
  @ViewChild('wrapper')
  wrapperElRef!: ElementRef;

  // eslint-disable-next-line @typescript-eslint/no-inferrable-types
  @Input() override placeholder: string = 'Select options';
  @Input() optionsType: DropDownType = 'list';

  @Input() selectData = false;
  // @Input() idSelection = '';


  expanded = false;
  localSelectedValue!: DropDownOption;
  localSelectedValues: DropDownOption[] = [];

  options: DropDownOption[] = [];
  groupedOptions: GroupedDropDownOption[] = [];

  filteredOptions: DropDownOption[] = [];
  filteredGroupOptions: GroupedDropDownOption[] = [];


  @Input() clearable = false;

  @Output() selectionChange = new EventEmitter<any>();
  @Output() selectionChangeData = new EventEmitter<any>();
  @Output() optionsToggled = new EventEmitter<any>();
  @Output() cleared = new EventEmitter<void>();


  constructor(private cdr: ChangeDetectorRef,
    fb: FormBuilder, translate: TranslateService) {
    super(fb, translate);
  }

  private _searchable = false;

  get searchable(): boolean {
    return this._searchable;
  }

  @Input() set searchable(v: boolean) {
    this._searchable = v;
  }

  private _items: DropDownOption[] = [];

  get items(): DropDownOption[] {
    return this._items;
  }

  @Input() set items(v: DropDownOption[]) {
    this._items = v;
    if (v) {
      this.options = v;
      // `0` is a real selection - the "All users" style sentinels use it - so a
      // plain truthy check would leave those labels unresolved. Matches the
      // test the selectedValue setter already applies.
      if (this.selectedValue || typeof this.selectedValue === 'number') {
        this.value = this.options.find((e) => e.value === this.selectedValue)?.name;
      }
      // Searchable dropdowns still start with the full list so opening them
      // without typing shows every option.
      this.filteredOptions = this.options;
    }
  }


  private _groupedItems: GroupedDropDownOption[] = [];

  get groupedItems(): GroupedDropDownOption[] {
    return this._groupedItems;
  }

  @Input() set groupedItems(v: GroupedDropDownOption[]) {
    this._groupedItems = v;
    if (v) {
      this.groupedOptions = v;
      if (this.selectedValue) {
        this.groupedOptions.forEach(group => {
          const selectedName = group.options.find((e) => e.value === this.selectedValue)?.name;
        });
      }
      this.filteredGroupOptions = this.groupedOptions;
    }
  }
  get selectedValue(): any {
    return this.localSelectedValue;
  }

  @Input() set selectedValue(value: any) {
    this.localSelectedValue = value;
    if (value || typeof(value) === 'number') {
      if (this.optionsType !== 'groupList') {
        this.value = this.options.find((e) => e.value === value)?.name;
      } else {
        this.groupedOptions.forEach(group => {
          const selectedName = group.options.find((e) => e.value === value)?.name;
          if (selectedName) {
            this.value = selectedName;
          }
        });
      }
    } else {
      this.clear();
    }
    this.syncSelectedFlags();
  }

  get empty(): boolean {
    const isEmpty = this.optionsType !== 'groupList' ? this.filteredOptions.length <= 0 : this.filteredGroupOptions.length <= 0;
    return isEmpty;
  }


  @HostListener('document:click', ['$event.target'])
  public onClick(target: any): void {
    const wrapperRef = this.wrapperElRef?.nativeElement;

    if (wrapperRef && !wrapperRef.contains(target)) {
      this.expanded = false;
    }
  }


  @HostListener('document:mousedown', ['$event.target'])
  public onClick2(target: any): void {
   const wrapperRef = this.wrapperElRef?.nativeElement;

   if (wrapperRef && !wrapperRef.contains(target)) {
     this.expanded = false;
   }
  }

  override ngOnInit(): void {
    super.ngOnInit();
    if (this.searchable) {
      this.valueChangedObservable.pipe(distinctUntilChanged()).subscribe((event: ValueChangeEvent) => {
        // Picking an option writes its name back into the field. That echo
        // reaches this stream just like typing does, so without this guard the
        // list would reopen filtered down to the option just selected.
        if (event.value && event.value === this.selectedOptionName) {
          return;
        }
        if (this.optionsType !== 'groupList') {
          if (event.value) {
            this.formStates.focused = true;
            this.filteredOptions = this.options.filter(e => e.name.toLowerCase().indexOf(event.value.toLowerCase()) > -1);
            this.expanded = !this.empty;
          } else {
            // Leave formStates.error alone: InputComponent has just set it from the
            // validators before emitting, and clearing it here hid the required error
            // on every empty searchable dropdown.
            this.filteredOptions = [...this.options];
          }
        } else {
          if (event.value) {
            this.formStates.focused = true;
            this.filteredGroupOptions = this.groupedOptions.map(item => {
              const newItem = {
                ...item,
                options: item.options.filter(e => e.name.toLowerCase().indexOf(event.value.toLowerCase()) > -1)
              };
             return newItem;
            });
            this.expanded = !this.empty;
          } else {
            this.filteredGroupOptions = [...this.groupedOptions];
          }
        }
      });
    }
  }

  override onBlur(): void {
    super.onBlur();
    if (this.searchable) {
      this.restoreDisplayValue();
    }
  }

  exist(value: string): DropDownOption | undefined {
    return this.options.find(e => e.value === value);
  }

  toggle(event: Event): void {
    if (!this.disabled) {
      // event.preventDefault();
      // event.stopPropagation();
      // When searching, clicking inside the text field must not close the list.
      if (this.searchable && this.expanded && event.target === this.inputElRef.nativeElement) {
        return;
      }
      this.expanded = !this.expanded && !this.empty;
      if (this.expanded) {
        this.inputElRef.nativeElement.focus();
        // this.onFocus();
        this.optionsToggled.emit(true);
      } else {
        this.inputElRef.nativeElement.blur();
        // this.onBlur();
      }
      this.cdr.detectChanges();

    }
  }

  select(option: DropDownOption): void {
    // this.localSelectedValue = option;
    this.selectedValue = option.value;
    if (this.optionsType !== 'groupList') {
    this.filteredOptions = [...this.options];

    } else {
      this.filteredGroupOptions = [...this.groupedOptions];
    }

    if (this.selectData) {
      this.selectionChange.emit(option.data);
    } else {
      this.selectionChange.emit(option.value);
      this.selectionChangeData.emit(option);
    }
  }

  optionsChanged(options: DropDownOption[]): void {
    this.value = options.map(e => e.name).join(', ');
    if (!this.value) {
      this.formStates.labelRaised = false;
    }
  }

  override clear(): void {
    this.value = null;
    this.formStates.labelRaised = false;
  }

  onClear(event: Event): void {
    event.stopPropagation();
    this.selectedValue = null;
    this.cleared.emit();
  }

  save(options: DropDownOption[]): void {
    this.value = options.map(e => e.name).join(', ');
  }

  onClosed(): void {
    this.expanded = false;
  }

  /**
   * Keeps every option's selected flag in step with the current selection.
   * Runs even when there is no selection: picking an "All" style option, whose
   * value is null, or clearing the field must un-highlight whatever was
   * highlighted before.
   */
  private syncSelectedFlags(): void {
    if (this.optionsType !== 'groupList') {
      this.options.forEach(e => {
        e.selected = e.value === this.localSelectedValue;
      });
    } else {
      this.groupedOptions.forEach(group => {
        group.options.forEach(e => {
          e.selected = e.value === this.localSelectedValue;
        });
      });
    }
  }

  /**
   * The label of the option currently selected, across both list shapes.
   */
  private get selectedOptionName(): string | undefined {
    if (this.optionsType !== 'groupList') {
      return this.options.find(e => e.value === this.localSelectedValue)?.name;
    }
    for (const group of this.groupedOptions) {
      const found = group.options.find(e => e.value === this.localSelectedValue);
      if (found) {
        return found.name;
      }
    }
    return undefined;
  }

  /**
   * Puts the selected option's label back into the field once a search ends
   * without a pick, so a stale search term is never left on screen.
   */
  private restoreDisplayValue(): void {
    this.value = this.selectedOptionName ?? null;
    this.filteredOptions = [...this.options];
  }
}
